import React, { useState, useEffect } from 'react';
import {
  BellIcon,
  CogIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { documentsAPI } from '../services/api';

const DocumentNotificationSettings = () => {
  const [settings, setSettings] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [typesResponse, notificationsResponse] = await Promise.all([
        documentsAPI.getDocumentTypes(),
        documentsAPI.getDocumentNotifications()
      ]);
      
      setDocumentTypes(typesResponse.data);
      setNotifications(notificationsResponse.data);
      
      // Initialize settings with default values if not found
      const defaultSettings = typesResponse.data.map(type => ({
        document_type_id: type.id,
        document_type_name: type.name,
        notification_days: type.notification_days || [90, 30, 14, 7, 1],
        email_notifications: true,
        dashboard_notifications: true,
        hr_notifications: true,
        manager_notifications: false,
        is_active: true
      }));
      
      setSettings(defaultSettings);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (documentTypeId, field, value) => {
    setSettings(prev => prev.map(setting => 
      setting.document_type_id === documentTypeId 
        ? { ...setting, [field]: value }
        : setting
    ));
  };

  const updateNotificationDays = (documentTypeId, days) => {
    const daysArray = days.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d) && d > 0);
    updateSetting(documentTypeId, 'notification_days', daysArray);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // In a real implementation, you would save to the backend
      // For now, we'll just show a success message
      alert('Notification settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const acknowledgeNotification = async (notificationId) => {
    try {
      await documentsAPI.acknowledgeNotification(notificationId);
      setNotifications(prev => prev.map(notif => 
        notif.id === notificationId 
          ? { ...notif, acknowledged_at: new Date().toISOString() }
          : notif
      ));
    } catch (error) {
      console.error('Error acknowledging notification:', error);
    }
  };

  const getNotificationTypeIcon = (type) => {
    switch (type) {
      case 'expired':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      case 'expiry_warning':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      default:
        return <InformationCircleIcon className="w-5 h-5 text-blue-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center">
          <BellIcon className="w-8 h-8 text-blue-600 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Document Notification Settings</h1>
            <p className="text-gray-600">Configure how and when notifications are sent for expiring documents</p>
          </div>
        </div>
      </div>

      {/* Active Notifications */}
      {notifications.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 mr-2" />
            Active Notifications ({notifications.filter(n => !n.acknowledged_at).length})
          </h2>
          
          <div className="space-y-3">
            {notifications.filter(n => !n.acknowledged_at).map(notification => (
              <div key={notification.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                <div className="flex items-center space-x-3">
                  {getNotificationTypeIcon(notification.notification_type)}
                  <div>
                    <p className="font-medium text-gray-900">
                      {notification.first_name} {notification.last_name} - {notification.document_type}
                    </p>
                    <p className="text-sm text-gray-600">
                      {notification.document_name} 
                      {notification.days_until_expiry !== null && (
                        <span className="ml-2 text-yellow-600">
                          {notification.days_until_expiry < 0 
                            ? `Expired ${Math.abs(notification.days_until_expiry)} days ago`
                            : notification.days_until_expiry === 0 
                            ? 'Expires today'
                            : `Expires in ${notification.days_until_expiry} days`
                          }
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => acknowledgeNotification(notification.id)}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Acknowledge
                </button>
              </div>
            ))}
            
            {notifications.filter(n => !n.acknowledged_at).length === 0 && (
              <div className="text-center py-4 text-gray-500">
                <CheckCircleIcon className="w-12 h-12 mx-auto text-green-500 mb-2" />
                <p>No active notifications</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notification Settings */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <CogIcon className="w-5 h-5 text-gray-600 mr-2" />
            Notification Configuration
          </h2>
          <button
            onClick={saveSettings}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        <div className="space-y-6">
          {settings.map(setting => (
            <div key={setting.document_type_id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium text-gray-900">{setting.document_type_name}</h3>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={setting.is_active}
                    onChange={(e) => updateSetting(setting.document_type_id, 'is_active', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">Enable notifications</span>
                </label>
              </div>

              {setting.is_active && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notification Days Before Expiry
                    </label>
                    <input
                      type="text"
                      value={setting.notification_days.join(', ')}
                      onChange={(e) => updateNotificationDays(setting.document_type_id, e.target.value)}
                      placeholder="e.g., 90, 30, 14, 7, 1"
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Comma-separated list of days before expiry to send notifications
                    </p>
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-700">Notification Methods</h4>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={setting.email_notifications}
                        onChange={(e) => updateSetting(setting.document_type_id, 'email_notifications', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-600">Email notifications</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={setting.dashboard_notifications}
                        onChange={(e) => updateSetting(setting.document_type_id, 'dashboard_notifications', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-600">Dashboard notifications</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={setting.hr_notifications}
                        onChange={(e) => updateSetting(setting.document_type_id, 'hr_notifications', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-600">HR team notifications</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={setting.manager_notifications}
                        onChange={(e) => updateSetting(setting.document_type_id, 'manager_notifications', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-600">Manager notifications</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Information Panel */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex">
          <InformationCircleIcon className="w-5 h-5 text-blue-400 mt-0.5 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-blue-800">How Notifications Work</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Notifications are automatically sent at 9 AM daily based on your configured settings</li>
                <li>Employees receive individual notifications about their expiring documents</li>
                <li>HR team receives a daily summary of all documents requiring attention</li>
                <li>Critical notifications (7 days or less) are sent with higher priority</li>
                <li>Weekly summaries are sent every Monday to HR managers</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentNotificationSettings;